import React, { useImperativeHandle } from "react";

import { CardTitle } from "../common";
import PropTypes from "prop-types";

// Utility function to normalize timestamp (if needed)
const normalizeTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const KVCard = React.forwardRef(({ card, topic, messages, w, h }, ref) => {
  const action = (setSelectedCard) => {
    setSelectedCard(card);
  };

  useImperativeHandle(ref, () => ({
    action,
  }));

  // Ensure messages is an array
  const safeMessages = messages && messages.length > 0 ? messages : [];
  // Use the last message from safeMessages, or fallback to a default object
  const effectiveLastMessage =
    safeMessages.length > 0
      ? safeMessages[safeMessages.length - 1]
      : { content: "{}", timestamp: null };

  // Parse the content of the last message safely
  let lastMessageJSON = {};
  try {
    lastMessageJSON = JSON.parse(effectiveLastMessage.content);
  } catch (e) {
    lastMessageJSON = {}; // Fallback to empty if parsing fails
  }

  // Determine size key
  const sizeKey = `${w}x${h}`;

  // Define rendering for each size
  const renderContent = () => {
    switch (sizeKey) {
      case "1x1":
        return (
          <div className="flex items-center justify-center w-full h-full">
            {/* Display topic name */}
            <CardTitle title={topic.name} />
          </div>
        );
      default:
        return (
          <div className="flex flex-col justify-between w-full h-full">
            <CardTitle title={topic.name} />
            {/* Key-Value Display */}
            <div className="overflow-y-auto max-h-full">
              {Object.entries(lastMessageJSON).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    {key}:
                  </span>
                  <span className="text-sm text-gray-800">{String(value)}</span>
                </div>
              ))}
            </div>
            {/* Timestamp */}
            {effectiveLastMessage.timestamp && (
              <p className="text-xs text-gray-500 mt-2">
                {normalizeTimestamp(effectiveLastMessage.timestamp)}
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="p-2 shadow-md cursor-pointer transition-transform duration-300 w-full h-full flex flex-col bg-gray-50">
      {renderContent()}
    </div>
  );
});

KVCard.variants = ["default", "table", "tree"];
KVCard.variantParameters = {
  default: {},
  table: {},
  tree: {},
};
KVCard.propTypes = {
  card: PropTypes.object.isRequired,
  topic: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
};

export default KVCard;
